import React, { useEffect, useState } from "react";
import { useAuthInfo } from "@propelauth/react";
import { fetchWithToken, postWithToken } from "../../api/apiBase";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { useToast} from "../ui/use-toast";

interface JoinableProject {
    project_id: string;
    name: string;
    number: string;
}

export default function JoinableProjects() {
    const { accessToken } = useAuthInfo();
    const {toast} = useToast();
    const [projects, setProjects] = useState<JoinableProject[]>([]);
    const [requested, setRequested] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!accessToken) return;
        fetchWithToken("/join-requests/find-joinable", accessToken)
            .then((data) => setProjects(data))
            .catch((err) => console.error("Failed to load joinable projects", err))
            .finally(() => setLoading(false));
    }, [accessToken]);

    const handleRequest = async (projectId: string) => {
        if (!accessToken) return;
        try {
            await postWithToken(
                "/join-requests/request",
                JSON.stringify({ project_id: projectId }),
                accessToken
            );
            const next = new Set(requested);
            next.add(projectId);
            setRequested(next);
        } catch (err) {
            console.error("Failed to request join", err);
            toast({
                        title: "Error",
                        duration: 5000,
                        description: err as string || "Failed to request",
                        variant: "destructive",
                    });
        }
    };

    if (loading || projects.length === 0) return null;

    return (
        <div className="mt-6">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
                Projects available to join
            </h3>
            <p className="text-sm text-gray-500 mb-3">
                Your company has been invited to these projects.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map((project) => (
                    <Card key={project.project_id} className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-sm">{project.name}</div>
                                {project.number && (
                                    <div className="text-xs text-gray-500">#{project.number}</div>
                                )}
                            </div>
                            <Button
                                size="sm"
                                variant={requested.has(project.project_id) ? "outline" : "default"}
                                disabled={requested.has(project.project_id)}
                                onClick={() => handleRequest(project.project_id)}
                            >
                                {requested.has(project.project_id) ? "Requested" : "Request to Join"}
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
